import { useState } from 'react'
import { DialogButton } from '..'
import VerlaufModalDialog from './VerlaufModalDialog'
import { useT } from '../../../i18n'
import { useAuth } from '../../../auth'

const MEDIA = { storageId: 101705, video: true }
const CONTENT_API = 'https://content-api.arkturian.com/api/v1'
const SURVEY_COLLECTION_ID = 5
const SLACK_WEBHOOK = '/api/slack-webhook'
const SURVEY_DONE_KEY = 'tschepp-survey-done'

interface SurveyDialogProps {
  open: boolean
  onClose: () => void
}

export default function SurveyDialog({ open, onClose }: SurveyDialogProps) {
  const t = useT()
  const { user } = useAuth()
  const [interests, setInterests] = useState<string[]>([])
  const [visit, setVisit] = useState('')
  const [device, setDevice] = useState('')
  const [wish, setWish] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const toggleInterest = (key: string) => {
    setInterests(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key])
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    try {
      const interestLabels = interests.map(k => t.dialog.survey.interestOptions[k as keyof typeof t.dialog.survey.interestOptions]).join(', ')
      const visitLabel = visit ? t.dialog.survey.visitOptions[visit as keyof typeof t.dialog.survey.visitOptions] : '—'
      const deviceLabel = device ? t.dialog.survey.deviceOptions[device as keyof typeof t.dialog.survey.deviceOptions] : '—'
      const storedEmail = localStorage.getItem('tschepp-email')
      const userName = user ? `${user.first_name} ${user.last_name}`.trim() : (storedEmail?.split('@')[0] ?? 'Anonymous')
      const userEmail = user?.email ?? storedEmail ?? 'survey@tschepp-ar.com'

      // 1. Save as "fragebogen" post in content-api + add to collection
      const postRes = await fetch(`${CONTENT_API}/posts/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: `Survey — ${userName} (${userEmail})`,
          content: JSON.stringify({
            respondent: { name: userName, email: userEmail, user_id: user?.id ?? null },
            questions: [
              { key: 'interests', question: t.dialog.survey.interestLabel, type: 'multiple_choice', answer: interests, answer_labels: interests.map(k => t.dialog.survey.interestOptions[k as keyof typeof t.dialog.survey.interestOptions]) },
              { key: 'visit', question: t.dialog.survey.visitLabel, type: 'single_choice', answer: visit || null, answer_label: visitLabel },
              { key: 'device', question: t.dialog.survey.deviceLabel, type: 'single_choice', answer: device || null, answer_label: deviceLabel },
              { key: 'wish', question: t.dialog.survey.wishLabel, type: 'free_text', answer: wish || null },
            ],
          }),
          content_type: 'json',
          status: 'published',
          author_id: user?.id ?? 'anonymous',
          author_name: userName,
          partner_id: 'tschepp-ar',
          metadata_json: {
            type: 'fragebogen',
            email: userEmail,
            source: 'tschepp-ar-web',
            submitted_at: new Date().toISOString(),
          },
        }),
      }).catch(() => null)

      // Add post to "TSCHEPP-AR Umfragen" collection
      if (postRes && postRes.ok) {
        const post = await postRes.json().catch(() => null)
        if (post?.id) {
          await fetch(`${CONTENT_API}/collections/${SURVEY_COLLECTION_ID}/posts/${post.id}`, {
            method: 'POST',
          }).catch(() => {})
        }
      }

      // 3. Slack notification (via nginx proxy to avoid CORS)
      await fetch(SLACK_WEBHOOK, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: `📋 *Neue TSCHEPP-AR Umfrage*\n*Von:* ${userName} (${userEmail})\n*Interessen:* ${interestLabels || '—'}\n*Besucht:* ${visitLabel}\n*Gerät:* ${deviceLabel}\n*Wunsch:* ${wish || '—'}`,
        }),
      }).catch(() => {})
    } catch {
      // Silent fail — don't block the user
    }

    localStorage.setItem(SURVEY_DONE_KEY, '1')
    setSubmitting(false)
    onClose()
  }

  const handleSkip = () => {
    localStorage.setItem(SURVEY_DONE_KEY, '1')
    onClose()
  }

  const opts = t.dialog.survey

  return (
    <VerlaufModalDialog open={open} onClose={handleSkip} theme="light" media={MEDIA}>
      <h3 className="sg-dialog__title">{opts.title}</h3>
      <p className="sg-vdialog__desc sg-vdialog__desc--light">{opts.description}</p>

      <div className="sg-survey">
        {/* Q1: Interests (multiple choice) */}
        <div className="sg-survey__question">
          <label className="sg-survey__label">{opts.interestLabel}</label>
          <div className="sg-survey__chips">
            {(['ar', 'flora', 'routes', 'stories'] as const).map(key => (
              <button
                key={key}
                className={`sg-survey__chip${interests.includes(key) ? ' sg-survey__chip--active' : ''}`}
                onClick={() => toggleInterest(key)}
                type="button"
              >
                {opts.interestOptions[key]}
              </button>
            ))}
          </div>
        </div>

        {/* Q2: Visit frequency (single choice) */}
        <div className="sg-survey__question">
          <label className="sg-survey__label">{opts.visitLabel}</label>
          <div className="sg-survey__chips">
            {(['never', 'once', 'regular', 'local'] as const).map(key => (
              <button
                key={key}
                className={`sg-survey__chip${visit === key ? ' sg-survey__chip--active' : ''}`}
                onClick={() => setVisit(key)}
                type="button"
              >
                {opts.visitOptions[key]}
              </button>
            ))}
          </div>
        </div>

        {/* Q3: Device (single choice) */}
        <div className="sg-survey__question">
          <label className="sg-survey__label">{opts.deviceLabel}</label>
          <div className="sg-survey__chips">
            {(['iphone', 'android'] as const).map(key => (
              <button
                key={key}
                className={`sg-survey__chip${device === key ? ' sg-survey__chip--active' : ''}`}
                onClick={() => setDevice(key)}
                type="button"
              >
                {opts.deviceOptions[key]}
              </button>
            ))}
          </div>
        </div>

        {/* Q4: Wish (free text) */}
        <div className="sg-survey__question">
          <label className="sg-survey__label">{opts.wishLabel}</label>
          <textarea
            className="sg-survey__textarea"
            placeholder={opts.wishPlaceholder}
            value={wish}
            onChange={e => setWish(e.target.value)}
            rows={3}
          />
        </div>
      </div>

      <DialogButton variant="primary" onClick={handleSubmit} disabled={submitting} style={{ marginBottom: 8 }}>
        {opts.submit}
      </DialogButton>
      <DialogButton variant="ghost" onClick={handleSkip}>
        {opts.skip}
      </DialogButton>
    </VerlaufModalDialog>
  )
}
